import type { BusinessType } from "@kivunova/kivufrontendcommon";

export type OrganizationLabelDefinition = {
  key: string;
  fallback: string;
};

export type OrganizationLabelDefinitions = {
  nameLabel: OrganizationLabelDefinition;
  informationTitle: OrganizationLabelDefinition;
  editButton: OrganizationLabelDefinition;
  editDetailsTitle: OrganizationLabelDefinition;
  detailsSection: OrganizationLabelDefinition;
  addressSection: OrganizationLabelDefinition;
  fetchingDetail: OrganizationLabelDefinition;
  notFoundTitle: OrganizationLabelDefinition;
  notFoundDescription: OrganizationLabelDefinition;
  nameRequired: OrganizationLabelDefinition;
  nameRequiredError: OrganizationLabelDefinition;
  pageTitle: OrganizationLabelDefinition;
  sidebarProfile: OrganizationLabelDefinition;
  employeesTitle: OrganizationLabelDefinition;
  staffTab: OrganizationLabelDefinition;
  adminRole: OrganizationLabelDefinition;
  reviewSectionChip: OrganizationLabelDefinition;
  registrationStepTitle: OrganizationLabelDefinition;
  registrationStepDescription: OrganizationLabelDefinition;
};

export type OrganizationDashboardLabels = {
  subtitle: OrganizationLabelDefinition;
  staffSectionTitle: OrganizationLabelDefinition;
  staffByStatusTitle: OrganizationLabelDefinition;
  staffByRoleTitle: OrganizationLabelDefinition;
  homeTitle: OrganizationLabelDefinition;
  homeBody: OrganizationLabelDefinition;
};

export type OrganizationRegistrationConfig = {
  /** Retail/MFG tenants see store onboarding copy during registration review. */
  showStoreOnboardingHint: boolean;
};

export type DashboardSection =
  | "revenueKpis"
  | "subscription"
  | "educationSummary"
  | "orders"
  | "salesInventory"
  | "products"
  | "productItemsByState"
  | "categories"
  | "stores"
  | "staff"
  | "customers"
  | "suppliers";

export type OrganizationDashboardConfig = {
  /** Hide store filter and store KPI sections (e.g. Education has no stores). */
  showStoreSections: boolean;
  /** Accordion sections and top KPI strip rendered for this business type. */
  sections: readonly DashboardSection[];
  labels: OrganizationDashboardLabels;
};

export type OrganizationVerbiageConfig = {
  labels: OrganizationLabelDefinitions;
  registration: OrganizationRegistrationConfig;
  dashboard: OrganizationDashboardConfig;
};

const COMPANY_LABELS: OrganizationLabelDefinitions = {
  nameLabel: { key: "company-name", fallback: "Company Name" },
  informationTitle: {
    key: "company-information",
    fallback: "Company Information"
  },
  editButton: { key: "edit-company", fallback: "Edit Company" },
  editDetailsTitle: {
    key: "edit-company-details",
    fallback: "Edit Company Details"
  },
  detailsSection: { key: "company-details", fallback: "Company Details" },
  addressSection: { key: "company-address", fallback: "Company Address" },
  fetchingDetail: {
    key: "company-fetching-detail",
    fallback: "Fetching company detail..."
  },
  notFoundTitle: {
    key: "company-not-found-title",
    fallback: "Company not found"
  },
  notFoundDescription: {
    key: "company-not-found-description",
    fallback:
      "The company you're looking for doesn't exist or has been removed. Please contact admin."
  },
  nameRequired: {
    key: "company-name-required",
    fallback: "Company name is required"
  },
  nameRequiredError: {
    key: "company-name-required-error-message",
    fallback: "Company name is required"
  },
  pageTitle: { key: "page-title-company", fallback: "Company" },
  sidebarProfile: { key: "sidebar-company", fallback: "Company" },
  employeesTitle: {
    key: "company-employees",
    fallback: "Company Employees"
  },
  staffTab: { key: "employee-tab", fallback: "Employees" },
  adminRole: { key: "user-role-company-admin", fallback: "Company admin" },
  reviewSectionChip: {
    key: "company-information-chip-label",
    fallback: "Company Info"
  },
  registrationStepTitle: {
    key: "registration-step-company",
    fallback: "Company Info"
  },
  registrationStepDescription: {
    key: "company-info-title",
    fallback: "Let's learn more about your company"
  }
};

const SCHOOL_LABELS: OrganizationLabelDefinitions = {
  nameLabel: { key: "school-name", fallback: "School name" },
  informationTitle: {
    key: "school-information",
    fallback: "School information"
  },
  editButton: { key: "edit-school", fallback: "Edit school" },
  editDetailsTitle: {
    key: "edit-school-details",
    fallback: "Edit school details"
  },
  detailsSection: { key: "school-details", fallback: "School details" },
  addressSection: { key: "school-address", fallback: "School address" },
  fetchingDetail: {
    key: "school-fetching-detail",
    fallback: "Fetching school detail..."
  },
  notFoundTitle: {
    key: "school-not-found-title",
    fallback: "School not found"
  },
  notFoundDescription: {
    key: "school-not-found-description",
    fallback:
      "The school you're looking for doesn't exist or has been removed. Please contact admin."
  },
  nameRequired: {
    key: "school-name-required",
    fallback: "School name is required"
  },
  nameRequiredError: {
    key: "school-name-required-error-message",
    fallback: "School name is required"
  },
  pageTitle: { key: "page-title-school", fallback: "School" },
  sidebarProfile: {
    key: "sidebar-school-profile",
    fallback: "School profile"
  },
  employeesTitle: { key: "school-employees", fallback: "School staff" },
  staffTab: { key: "school-staff-tab", fallback: "Staff" },
  adminRole: { key: "user-role-school-admin", fallback: "School admin" },
  reviewSectionChip: {
    key: "school-information-chip-label",
    fallback: "School info"
  },
  registrationStepTitle: {
    key: "registration-step-school",
    fallback: "School info"
  },
  registrationStepDescription: {
    key: "school-info-title",
    fallback: "Let's learn more about your school"
  }
};

const RETAIL_DASHBOARD_SECTIONS: readonly DashboardSection[] = [
  "revenueKpis",
  "subscription",
  "orders",
  "products",
  "productItemsByState",
  "categories",
  "stores",
  "staff",
  "customers",
  "suppliers"
];

const EDUCATION_DASHBOARD_SECTIONS: readonly DashboardSection[] = [
  "educationSummary",
  "subscription",
  "staff"
];

const COMPANY_DASHBOARD: OrganizationDashboardConfig = {
  showStoreSections: true,
  sections: RETAIL_DASHBOARD_SECTIONS,
  labels: {
    subtitle: {
      key: "dashboard-subtitle",
      fallback: "Overview of your business metrics"
    },
    staffSectionTitle: {
      key: "dashboard-section-employees",
      fallback: "Employees"
    },
    staffByStatusTitle: {
      key: "dashboard-pie-employees-by-status",
      fallback: "Employees by status"
    },
    staffByRoleTitle: {
      key: "dashboard-pie-employees-by-role",
      fallback: "Employees by role"
    },
    homeTitle: { key: "dashboard-title", fallback: "Dashboard" },
    homeBody: {
      key: "dashboard-subtitle",
      fallback: "Overview of your business metrics"
    }
  }
};

const EDUCATION_DASHBOARD: OrganizationDashboardConfig = {
  showStoreSections: false,
  sections: EDUCATION_DASHBOARD_SECTIONS,
  labels: {
    subtitle: {
      key: "education-dashboard-subtitle",
      fallback: "Overview of your school operations"
    },
    staffSectionTitle: {
      key: "dashboard-section-school-staff",
      fallback: "School staff"
    },
    staffByStatusTitle: {
      key: "dashboard-pie-school-staff-by-status",
      fallback: "Staff by status"
    },
    staffByRoleTitle: {
      key: "dashboard-pie-school-staff-by-role",
      fallback: "Staff by role"
    },
    homeTitle: { key: "education-home-title", fallback: "Education" },
    homeBody: {
      key: "education-home-body",
      fallback:
        "Manage students, fees, procurement, expenses, and stock usage for your school."
    }
  }
};

const COMPANY_VERBIAGE: OrganizationVerbiageConfig = {
  labels: COMPANY_LABELS,
  registration: { showStoreOnboardingHint: true },
  dashboard: COMPANY_DASHBOARD
};

const EDUCATION_VERBIAGE: OrganizationVerbiageConfig = {
  labels: SCHOOL_LABELS,
  registration: { showStoreOnboardingHint: false },
  dashboard: EDUCATION_DASHBOARD
};

/** Default tenant org copy — used when business type is unknown or not yet configured. */
export const DEFAULT_ORGANIZATION_VERBIAGE = COMPANY_VERBIAGE;

/**
 * Per-industry organization verbiage. Add a new entry when onboarding a business type
 * that uses different org entity naming (school, clinic, plant, etc.).
 */
export const ORGANIZATION_VERBIAGE_BY_BUSINESS_TYPE: Partial<
  Record<BusinessType, OrganizationVerbiageConfig>
> = {
  RETAIL: COMPANY_VERBIAGE,
  MANUFACTURING: COMPANY_VERBIAGE,
  SERVICES: COMPANY_VERBIAGE,
  HEALTHCARE: COMPANY_VERBIAGE,
  NONPROFIT: COMPANY_VERBIAGE,
  EDUCATION: EDUCATION_VERBIAGE
};

export const resolveOrganizationVerbiage = (
  businessType?: string | null
): OrganizationVerbiageConfig => {
  if (!businessType) {
    return DEFAULT_ORGANIZATION_VERBIAGE;
  }

  return (
    ORGANIZATION_VERBIAGE_BY_BUSINESS_TYPE[businessType as BusinessType] ??
    DEFAULT_ORGANIZATION_VERBIAGE
  );
};

export const isDashboardSectionVisible = (
  config: OrganizationDashboardConfig,
  section: DashboardSection
) => config.sections.includes(section);

export type ResolvedOrganizationLabels = {
  businessType: string | null;
  registration: OrganizationRegistrationConfig;
  dashboard: OrganizationDashboardConfig;
  nameLabel: string;
  informationTitle: string;
  editButton: string;
  editDetailsTitle: string;
  detailsSection: string;
  addressSection: string;
  fetchingDetail: string;
  notFoundTitle: string;
  notFoundDescription: string;
  nameRequired: string;
  nameRequiredError: string;
  pageTitle: string;
  sidebarProfile: string;
  employeesTitle: string;
  staffTab: string;
  adminRole: string;
  reviewSectionChip: string;
  registrationStepTitle: string;
  registrationStepDescription: string;
  dashboardSubtitle: string;
  dashboardStaffSectionTitle: string;
  dashboardStaffByStatusTitle: string;
  dashboardStaffByRoleTitle: string;
  dashboardHomeTitle: string;
  dashboardHomeBody: string;
};

const translateLabel = (
  t: (key: string, fallback: string) => string,
  definition: OrganizationLabelDefinition
) => t(definition.key, definition.fallback);

export const translateOrganizationLabels = (
  t: (key: string, fallback: string) => string,
  businessType?: string | null
): ResolvedOrganizationLabels => {
  const verbiage = resolveOrganizationVerbiage(businessType);
  const { labels, dashboard } = verbiage;

  return {
    businessType: businessType ?? null,
    registration: verbiage.registration,
    dashboard,
    nameLabel: translateLabel(t, labels.nameLabel),
    informationTitle: translateLabel(t, labels.informationTitle),
    editButton: translateLabel(t, labels.editButton),
    editDetailsTitle: translateLabel(t, labels.editDetailsTitle),
    detailsSection: translateLabel(t, labels.detailsSection),
    addressSection: translateLabel(t, labels.addressSection),
    fetchingDetail: translateLabel(t, labels.fetchingDetail),
    notFoundTitle: translateLabel(t, labels.notFoundTitle),
    notFoundDescription: translateLabel(t, labels.notFoundDescription),
    nameRequired: translateLabel(t, labels.nameRequired),
    nameRequiredError: translateLabel(t, labels.nameRequiredError),
    pageTitle: translateLabel(t, labels.pageTitle),
    sidebarProfile: translateLabel(t, labels.sidebarProfile),
    employeesTitle: translateLabel(t, labels.employeesTitle),
    staffTab: translateLabel(t, labels.staffTab),
    adminRole: translateLabel(t, labels.adminRole),
    reviewSectionChip: translateLabel(t, labels.reviewSectionChip),
    registrationStepTitle: translateLabel(t, labels.registrationStepTitle),
    registrationStepDescription: translateLabel(
      t,
      labels.registrationStepDescription
    ),
    dashboardSubtitle: translateLabel(t, dashboard.labels.subtitle),
    dashboardStaffSectionTitle: translateLabel(
      t,
      dashboard.labels.staffSectionTitle
    ),
    dashboardStaffByStatusTitle: translateLabel(
      t,
      dashboard.labels.staffByStatusTitle
    ),
    dashboardStaffByRoleTitle: translateLabel(
      t,
      dashboard.labels.staffByRoleTitle
    ),
    dashboardHomeTitle: translateLabel(t, dashboard.labels.homeTitle),
    dashboardHomeBody: translateLabel(t, dashboard.labels.homeBody)
  };
};
